import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { fmtDate } from '../../lib/constants';
import toast from 'react-hot-toast';

const TABS = [
  { key:'church',   icon:'🏛️', label:'Church Profile' },
  { key:'members',  icon:'👥', label:'Member Settings' },
  { key:'finance',  icon:'💰', label:'Financial Settings' },
  { key:'system',   icon:'⚙️', label:'System Preferences' },
  { key:'activity', icon:'📋', label:'Activity Log' },
];

const ACTION_COLORS = {
  created:  { bg:'#E8F5E9', color:'#1B5E20', icon:'➕' },
  updated:  { bg:'#E3F2FD', color:'#003399', icon:'✏️' },
  deleted:  { bg:'#FFEBEE', color:'#CC0000', icon:'🗑️' },
  login:    { bg:'#F3E5F5', color:'#6A1B9A', icon:'🔐' },
  logout:   { bg:'#ECEFF1', color:'#546E7A', icon:'⏏️' },
  viewed:   { bg:'#FFF8E0', color:'#C47A00', icon:'👁️' },
  setting:  { bg:'#E0F7FA', color:'#00695C', icon:'⚙️' },
};

const ENTITY_ICONS = {
  member:'👤', contribution:'💰', expense:'💸', service:'📅',
  user:'🔑', setting:'⚙️', inventory:'📦', household:'🏠',
};

export default function SettingsPage() {
  const { profile } = useAuth();
  const [tab,      setTab]      = useState('church');
  const [settings, setSettings] = useState({});
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [edited,   setEdited]   = useState({});

  // Activity log state
  const [logs,      setLogs]      = useState([]);
  const [logsLoad,  setLogsLoad]  = useState(false);
  const [actionF,   setActionF]   = useState('');
  const [entityF,   setEntityF]   = useState('');
  const [dateF,     setDateF]     = useState('');
  const [logSearch, setLogSearch] = useState('');
  const [logPage,   setLogPage]   = useState(0);
  const LOG_PAGE_SIZE = 30;

  useEffect(() => { loadSettings(); }, []);
  useEffect(() => { if (tab === 'activity') loadLogs(); }, [tab, actionF, entityF, dateF]);

  async function loadSettings() {
    setLoading(true);
    const { data } = await supabase.from('church_settings').select('*').order('category');
    const map = {};
    (data || []).forEach(s => { map[s.key] = s.value; });
    setSettings(map);
    setLoading(false);
  }

  async function loadLogs() {
    setLogsLoad(true);
    let q = supabase.from('activity_log').select('*').order('created_at', { ascending: false }).limit(200);
    if (actionF) q = q.eq('action', actionF);
    if (entityF) q = q.eq('entity_type', entityF);
    if (dateF)   q = q.gte('created_at', `${dateF}T00:00:00`).lte('created_at', `${dateF}T23:59:59`);
    const { data } = await q;
    setLogs(data || []);
    setLogsLoad(false);
    setLogPage(0);
  }

  function set(key, val) {
    setSettings(s => ({ ...s, [key]: val }));
    setEdited(e => ({ ...e, [key]: val }));
  }

  async function saveSettings() {
    if (!Object.keys(edited).length) { toast('No changes to save'); return; }
    setSaving(true);
    const updates = Object.entries(edited).map(([key, value]) => ({
      key, value,
      updated_at: new Date().toISOString(),
      updated_by: profile?.id || null,
    }));
    const { error } = await supabase.from('church_settings').upsert(updates, { onConflict: 'key' });
    if (error) { toast.error(error.message); setSaving(false); return; }

    // Log the settings change
    await supabase.from('activity_log').insert({
      user_id:     profile?.id,
      user_email:  profile?.email || '',
      action:      'updated',
      entity_type: 'setting',
      entity_name: `${Object.keys(edited).length} setting(s) updated`,
      details:     { keys: Object.keys(edited) },
    });

    toast.success(`${Object.keys(edited).length} setting(s) saved ✅`);
    setEdited({});
    setSaving(false);
  }

  // Filtered + paginated logs
  const filteredLogs = logs.filter(l => {
    if (!logSearch) return true;
    return (l.entity_name || '').toLowerCase().includes(logSearch.toLowerCase()) ||
           (l.user_email  || '').toLowerCase().includes(logSearch.toLowerCase()) ||
           (l.action      || '').toLowerCase().includes(logSearch.toLowerCase());
  });
  const pagedLogs   = filteredLogs.slice(logPage * LOG_PAGE_SIZE, (logPage + 1) * LOG_PAGE_SIZE);
  const totalPages  = Math.ceil(filteredLogs.length / LOG_PAGE_SIZE);

  const hasEdits = Object.keys(edited).length > 0;

  const F = ({ label, k, type='text', placeholder='', hint='' }) => (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <input className="form-input" type={type}
        value={settings[k] || ''}
        onChange={e => set(k, e.target.value)}
        placeholder={placeholder}
        style={{ borderColor: edited[k] ? 'var(--blue)' : '' }}
      />
      {hint && <div style={{ fontSize:11, color:'var(--text3)', marginTop:3 }}>{hint}</div>}
    </div>
  );

  const Sel = ({ label, k, options, hint='' }) => (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <select className="form-select" value={settings[k] || ''}
        onChange={e => set(k, e.target.value)}
        style={{ borderColor: edited[k] ? 'var(--blue)' : '' }}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      {hint && <div style={{ fontSize:11, color:'var(--text3)', marginTop:3 }}>{hint}</div>}
    </div>
  );

  const Toggle = ({ label, k, hint='' }) => (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 0', borderBottom:'1px solid var(--border)' }}>
      <div>
        <div style={{ fontWeight:600, fontSize:13 }}>{label}</div>
        {hint && <div style={{ fontSize:11, color:'var(--text3)', marginTop:2 }}>{hint}</div>}
      </div>
      <label style={{ position:'relative', display:'inline-block', width:44, height:24, cursor:'pointer', flexShrink:0 }}>
        <input type="checkbox" checked={settings[k]==='true'}
          onChange={e => set(k, e.target.checked ? 'true' : 'false')}
          style={{ opacity:0, width:0, height:0 }} />
        <span style={{
          position:'absolute', inset:0, borderRadius:24, transition:'.3s',
          background: settings[k]==='true' ? 'var(--blue)' : '#ccc',
        }}>
          <span style={{
            position:'absolute', width:18, height:18, borderRadius:'50%', background:'white',
            top:3, left: settings[k]==='true' ? 23 : 3, transition:'.3s',
            boxShadow:'0 1px 3px rgba(0,0,0,.3)',
          }} />
        </span>
      </label>
    </div>
  );

  if (loading) return <div style={{ textAlign:'center', padding:48, color:'var(--text3)' }}>Loading settings…</div>;

  return (
    <div>
      {/* Header */}
      <div className="flex-between mb-4">
        <div>
          <h1 style={{ fontSize:20, fontWeight:800 }}>⚙️ Settings</h1>
          <div style={{ fontSize:13, color:'var(--text3)', marginTop:2 }}>
            Church configuration, preferences and activity log
          </div>
        </div>
        {hasEdits && tab !== 'activity' && (
          <button className="btn btn-primary" onClick={saveSettings} disabled={saving}>
            {saving ? 'Saving…' : `💾 Save ${Object.keys(edited).length} Change(s)`}
          </button>
        )}
      </div>

      {/* Unsaved changes banner */}
      {hasEdits && tab !== 'activity' && (
        <div style={{ background:'#FFF8E0', border:'1.5px solid #C47A00', borderRadius:10, padding:'10px 16px', marginBottom:16, display:'flex', alignItems:'center', justifyContent:'space-between', fontSize:13 }}>
          <span style={{ color:'#C47A00', fontWeight:600 }}>⚠️ You have {Object.keys(edited).length} unsaved change(s)</span>
          <div className="flex gap-2">
            <button className="btn btn-sm btn-ghost" onClick={() => { setEdited({}); loadSettings(); }}>Discard</button>
            <button className="btn btn-sm" style={{ background:'#C47A00', color:'white', border:'none' }} onClick={saveSettings} disabled={saving}>
              {saving ? 'Saving…' : '💾 Save Now'}
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs mb-4">
        {TABS.map(t => (
          <button key={t.key} className={`tab${tab===t.key?' active':''}`} onClick={() => setTab(t.key)}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ══ CHURCH PROFILE ═══════════════════════════════════════ */}
      {tab==='church' && (
        <div className="card" style={{ maxWidth:700 }}>
          <div style={{ padding:'14px 18px', background:'var(--blue-dark)', color:'white', fontWeight:700, fontSize:14 }}>
            🏛️ Church Profile
          </div>
          <div className="card-pad">
            <div className="grid-2">
              <F label="Church Name *"    k="church_name"    placeholder="PCEA Thingati Church"/>
              <F label="Year Founded"     k="church_founded" type="number" placeholder="1950"/>
              <F label="Physical Address" k="church_address" placeholder="Thingati, Kiambu County"/>
              <F label="County"           k="church_county"  placeholder="Kiambu County"/>
              <F label="Church Email"     k="church_email"   type="email" placeholder="info@pceathingati.org"/>
              <F label="Church Phone"     k="church_phone"   placeholder="+254700000000"/>
              <F label="P.O. Box"         k="church_po_box"  placeholder="P.O. Box 1234"/>
              <F label="Website"          k="church_website" placeholder="https://pceathingati.org"/>
            </div>
            <div style={{ marginTop:16, padding:'12px 16px', background:'var(--bg)', borderRadius:8, fontSize:12, color:'var(--text3)' }}>
              💡 These details appear on PDF reports, letter headers and the system footer.
            </div>
          </div>
        </div>
      )}

      {/* ══ MEMBER SETTINGS ══════════════════════════════════════ */}
      {tab==='members' && (
        <div className="card" style={{ maxWidth:700 }}>
          <div style={{ padding:'14px 18px', background:'var(--blue-dark)', color:'white', fontWeight:700, fontSize:14 }}>
            👥 Member Settings
          </div>
          <div className="card-pad">
            <div className="grid-2">
              <F label="Member Number Prefix" k="member_number_prefix" placeholder="PTCM"
                hint="e.g. PTCM → member numbers like PTCM1001, PTCM1002"/>
              <F label="Starting Number" k="member_number_start" type="number" placeholder="1001"
                hint="New members start from this number"/>
              <Sel label="Default Membership Type" k="default_membership" options={[
                ['Pending','Pending'],['Full Member','Full Member'],['Associate Member','Associate Member'],
                ['Adherent','Adherent'],['Communicant','Communicant'],['Baptised Child','Baptised Child'],
              ]} hint="Applied to newly added members"/>
            </div>
            <div style={{ marginTop:16, borderTop:'1px solid var(--border)', paddingTop:16 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'var(--text3)', textTransform:'uppercase', letterSpacing:.5, marginBottom:12 }}>Required Fields</div>
              <Toggle label="Require Phone Number"  k="require_phone"    hint="If off, phone is optional (recommended for children)"/>
              <Toggle label="Require District"       k="require_district" hint="Members must be assigned to a district"/>
              <Toggle label="Auto-approve Self-Registrations" k="auto_approve" hint="If on, members who register online are immediately active without admin approval"/>
            </div>
          </div>
        </div>
      )}

      {/* ══ FINANCIAL SETTINGS ═══════════════════════════════════ */}
      {tab==='finance' && (
        <div className="card" style={{ maxWidth:700 }}>
          <div style={{ padding:'14px 18px', background:'var(--blue-dark)', color:'white', fontWeight:700, fontSize:14 }}>
            💰 Financial Settings
          </div>
          <div className="card-pad">
            <div className="grid-2">
              <Sel label="Currency" k="currency" options={[['KES','KES — Kenyan Shilling'],['USD','USD — US Dollar'],['GBP','GBP — British Pound']]}/>
              <Sel label="Financial Year Start" k="financial_year_start" options={[
                ['1','January'],['2','February'],['3','March'],['4','April'],
                ['5','May'],['6','June'],['7','July'],['8','August'],
                ['9','September'],['10','October'],['11','November'],['12','December'],
              ]} hint="When does the church financial year start?"/>
              <F label="Petty Cash Float Limit (KES)" k="petty_cash_limit" type="number" placeholder="10000"
                hint="Maximum amount allowed in petty cash at any time"/>
              <F label="Budget Alert Threshold (%)" k="budget_alert_pct" type="number" placeholder="80"
                hint="Warn when a category reaches this % of its budget"/>
            </div>
            <div style={{ marginTop:16, padding:'12px 16px', background:'#E8F5E9', borderRadius:8, fontSize:12, color:'#1B5E20' }}>
              💡 Currency setting affects all reports and PDF exports. Changing it does not convert existing data.
            </div>
          </div>
        </div>
      )}

      {/* ══ SYSTEM PREFERENCES ═══════════════════════════════════ */}
      {tab==='system' && (
        <div style={{ maxWidth:700 }}>
          <div className="card mb-4">
            <div style={{ padding:'14px 18px', background:'var(--blue-dark)', color:'white', fontWeight:700, fontSize:14 }}>
              ⚙️ System Preferences
            </div>
            <div className="card-pad">
              <div className="grid-2">
                <Sel label="Date Format" k="date_format" options={[
                  ['DD/MM/YYYY','DD/MM/YYYY (e.g. 03/06/2026)'],
                  ['MM/DD/YYYY','MM/DD/YYYY (e.g. 06/03/2026)'],
                  ['YYYY-MM-DD','YYYY-MM-DD (e.g. 2026-06-03)'],
                ]}/>
                <Sel label="Timezone" k="timezone" options={[
                  ['Africa/Nairobi','Africa/Nairobi (EAT, UTC+3)'],
                  ['UTC','UTC'],
                ]}/>
                <Sel label="Records Per Page" k="items_per_page" options={[
                  ['25','25'],['50','50'],['100','100'],['200','200'],
                ]} hint="How many rows shown in member and contribution lists"/>
                <F label="Session Timeout (minutes)" k="session_timeout" type="number" placeholder="480"
                  hint="How long before an inactive user is logged out (480 = 8 hours)"/>
              </div>
            </div>
          </div>

          {/* Database info */}
          <div className="card mb-4">
            <div style={{ padding:'14px 18px', background:'var(--blue-dark)', color:'white', fontWeight:700, fontSize:14 }}>
              🗄️ System Information
            </div>
            <div className="card-pad">
              {[
                ['CMS Version',          'PCEA Thingati CMS v2.0'],
                ['Database',             'Supabase PostgreSQL (Singapore)'],
                ['Frontend',             'React 18 on Vercel'],
                ['Auth Provider',        'Supabase Auth'],
                ['Last Settings Update', new Date().toLocaleDateString('en-KE')],
              ].map(([l,v])=>(
                <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid var(--border)', fontSize:13 }}>
                  <span style={{ color:'var(--text3)' }}>{l}</span>
                  <span style={{ fontWeight:600 }}>{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Quick actions */}
          <div className="card">
            <div style={{ padding:'14px 18px', background:'var(--blue-dark)', color:'white', fontWeight:700, fontSize:14 }}>
              🔧 Quick Actions
            </div>
            <div className="card-pad">
              {[
                { label:'Reload Schema Cache',  desc:'Fix "column not found in schema cache" errors',  color:'#003399', action:async()=>{ await supabase.rpc('reload_schema').catch(()=>{}); toast.success('Schema cache reload requested'); } },
                { label:'Clear Activity Log',   desc:'Delete all activity log entries (cannot undo)',   color:'#CC0000', action:async()=>{ if(!window.confirm('Clear all activity logs? This cannot be undone.'))return; await supabase.from('activity_log').delete().neq('id','00000000-0000-0000-0000-000000000000'); toast.success('Activity log cleared'); loadLogs(); } },
              ].map(({label,desc,color,action})=>(
                <div key={label} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 0', borderBottom:'1px solid var(--border)' }}>
                  <div>
                    <div style={{ fontWeight:600, fontSize:13 }}>{label}</div>
                    <div style={{ fontSize:11, color:'var(--text3)', marginTop:2 }}>{desc}</div>
                  </div>
                  <button className="btn btn-sm" style={{ background:`${color}22`, color, border:`1px solid ${color}44`, fontWeight:700 }} onClick={action}>
                    Run
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ══ ACTIVITY LOG ═════════════════════════════════════════ */}
      {tab==='activity' && (
        <div>
          {/* Stats */}
          <div className="grid-4 mb-4">
            {[
              ['📋', 'Total Events',   logs.length,                                         'blue' ],
              ['➕', 'Created',        logs.filter(l=>l.action==='created').length,          'green'],
              ['✏️', 'Updated',        logs.filter(l=>l.action==='updated').length,          'amber'],
              ['🗑️', 'Deleted',        logs.filter(l=>l.action==='deleted').length,          'red'  ],
            ].map(([ic,l,v,c])=>(
              <div key={l} className="stat-card">
                <div className={`stat-icon ${c}`}>{ic}</div>
                <div className="stat-value">{v}</div>
                <div className="stat-label">{l}</div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="card mb-4">
            <div className="card-pad" style={{ paddingTop:10, paddingBottom:10 }}>
              <div className="flex gap-2 flex-wrap">
                <div className="search-bar" style={{ flex:'1 1 180px' }}>
                  🔍
                  <input value={logSearch} onChange={e=>setLogSearch(e.target.value)} placeholder="Search name, email or action…"/>
                  {logSearch&&<button onClick={()=>setLogSearch('')} style={{background:'none',border:'none',color:'var(--text3)',cursor:'pointer',fontSize:16}}>✕</button>}
                </div>
                <select className="form-select" style={{ width:'auto' }} value={actionF} onChange={e=>setActionF(e.target.value)}>
                  <option value="">All Actions</option>
                  {['created','updated','deleted','login','logout'].map(a=><option key={a} value={a} style={{textTransform:'capitalize'}}>{a.charAt(0).toUpperCase()+a.slice(1)}</option>)}
                </select>
                <select className="form-select" style={{ width:'auto' }} value={entityF} onChange={e=>setEntityF(e.target.value)}>
                  <option value="">All Types</option>
                  {['member','contribution','expense','service','user','setting','inventory'].map(e=><option key={e} value={e} style={{textTransform:'capitalize'}}>{e.charAt(0).toUpperCase()+e.slice(1)}</option>)}
                </select>
                <input className="form-input" type="date" value={dateF} onChange={e=>setDateF(e.target.value)} style={{ width:'auto' }}/>
                {dateF&&<button className="btn btn-ghost btn-sm" onClick={()=>setDateF('')}>Clear Date</button>}
                <span style={{ fontSize:12, color:'var(--text3)', alignSelf:'center' }}>{filteredLogs.length} events</span>
              </div>
            </div>
          </div>

          {/* Log table */}
          {logsLoad ? (
            <div style={{ textAlign:'center', padding:32, color:'var(--text3)' }}>Loading activity log…</div>
          ) : (
            <>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Date / Time</th>
                      <th>Action</th>
                      <th>Type</th>
                      <th>Description</th>
                      <th>User</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedLogs.length===0 && (
                      <tr><td colSpan={6} style={{ textAlign:'center', padding:32, color:'var(--text3)' }}>
                        No activity recorded yet. Actions like adding members and recording contributions will appear here automatically.
                      </td></tr>
                    )}
                    {pagedLogs.map(log => {
                      const ac = ACTION_COLORS[log.action] || { bg:'#F5F5F5', color:'#546E7A', icon:'•' };
                      const ei = ENTITY_ICONS[log.entity_type] || '•';
                      return (
                        <tr key={log.id}>
                          <td style={{ whiteSpace:'nowrap', fontSize:11, color:'var(--text3)' }}>
                            {new Date(log.created_at).toLocaleDateString('en-KE', { day:'numeric', month:'short', year:'numeric' })}
                            <br/>
                            {new Date(log.created_at).toLocaleTimeString('en-KE', { hour:'2-digit', minute:'2-digit' })}
                          </td>
                          <td>
                            <span style={{ fontSize:11, padding:'3px 10px', borderRadius:100, background:ac.bg, color:ac.color, fontWeight:700, whiteSpace:'nowrap' }}>
                              {ac.icon} {log.action}
                            </span>
                          </td>
                          <td>
                            <span style={{ fontSize:12 }}>{ei} {log.entity_type}</span>
                          </td>
                          <td style={{ fontWeight:600, fontSize:13, maxWidth:240, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {log.entity_name || '—'}
                          </td>
                          <td style={{ fontSize:11, color:'var(--text3)' }}>
                            {log.user_email || 'System'}
                          </td>
                          <td style={{ fontSize:11, color:'var(--text3)', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {log.details && Object.keys(log.details).length > 0
                              ? Object.entries(log.details).slice(0,2).map(([k,v])=>`${k}: ${v}`).join(' · ')
                              : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:8, marginTop:16 }}>
                  <button className="btn btn-sm btn-ghost" onClick={()=>setLogPage(p=>Math.max(0,p-1))} disabled={logPage===0}>← Previous</button>
                  <span style={{ fontSize:13, color:'var(--text3)' }}>
                    Page {logPage+1} of {totalPages} · {filteredLogs.length} total
                  </span>
                  <button className="btn btn-sm btn-ghost" onClick={()=>setLogPage(p=>Math.min(totalPages-1,p+1))} disabled={logPage>=totalPages-1}>Next →</button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}